import { useState } from 'react'
import { catalog as catalogApi, leases as leaseApi } from '@/api/endpoints'
import type { LeaseApproval, ReviewItem } from '@/api/types'
import { Err } from '@/components/Err'
import { Facts } from '@/components/Facts'
import { NumberField } from '@/components/NumberField'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { usePoll } from '@/hooks/usePoll'
import { catalogModels, gpuMemoryFloorGb, gpuMemoryFloorMb } from '@/domain/flavor'
import { MAX_GPUS } from '@/domain/lease'
import { DASH, size, sizeMb } from '@/ui/format'
import { SELECT } from '../select'

// GPU 신청을 통과시키는 자리. 신청한 사양이 위에 서고, 그 아래에서 최고 관리자가 카드의 모델과 장 수를
// 고쳐 승인한다 — 카드 하나 때문에 반려하면 신청자가 처음부터 다시 적는다. 고치지 않고 누르면 신청한 그대로 간다.
//
// 메모리도 여기서 고친다(F-101). 카드의 하한이 신청자의 메모리 쿼터를 넘으면 신청은 쿼터에 막히지 않고 이리 오고,
// 메모리를 정하는 것은 승인하는 사람이다 — 직위의 한도는 묻지 않는다. 칸의 값은 카드가 요구하는 메모리를 변경 창들과
// 같은 산수로 재서 그 아래면 그 자리에서 막는다 — 재지 않으면 승인자는 숫자 없는 거절문을 읽는다. 견주는 것은
// 백엔드처럼 MB 다. 한 장의 메모리는 `/catalog` 의 모델 항목이 말한다.
//
// **카드는 카탈로그를 읽은 뒤에만 고친다** — 읽기 전에 고친 카드는 잴 수 없어 거절로 알게 된다.
// 승인 자체는 막지 않는다: 신청한 그대로는 신청이 이미 그 하한을 지났고, 그 읽기의 실패로 승인을 막으면 고칠 곳 없는
// 막힘이 된다.

const CATALOG_POLL_MS = 60000
const COUNTS = Array.from({ length: MAX_GPUS }, (_, i) => i + 1)

export function GpuApproveDialog({ item, open, onClose, onDone }: { item: ReviewItem; open: boolean; onClose: () => void; onDone: () => void }) {
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{item.subject_name} GPU 승인</DialogTitle>
        </DialogHeader>
        {open ? <ApproveForm item={item} onClose={onClose} onDone={onDone} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function ApproveForm({ item, onClose, onDone }: { item: ReviewItem; onClose: () => void; onDone: () => void }) {
  const d = item.detail
  const askedGpus = d.gpus ?? 1
  const askedModel = d.gpu_model ?? ''
  const [model, setModel] = useState(askedModel)
  const [gpus, setGpus] = useState(askedGpus)
  // 신청의 메모리(GB). 비우면 신청 그대로다 — placeholder 가 그 값을 말한다.
  const askedMemoryGb = d.memory_mb !== undefined ? String(Math.ceil(d.memory_mb / 1024)) : ''
  const [memoryGb, setMemoryGb] = useState(askedMemoryGb)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const catalog = usePoll(() => catalogApi.get(), CATALOG_POLL_MS)
  const measured = catalog.data !== null

  // 고를 수 있는 모델은 이 설치가 카드를 가진 모델이고, 남은 카드가 없는 모델도 선다 —
  // 자리가 없어도 승인은 남고 자리를 기다린다. 신청한 모델이 목록에 없으면 그 이름만 맨 앞에 선다.
  const models = catalogModels(catalog.data?.gpus ?? [], askedModel)

  // 보내는 것은 고친 쪽뿐이다. 아무것도 고치지 않으면 본문 없이 신청 그대로 통과시킨다.
  const body: LeaseApproval = {}
  if (model !== askedModel) body.gpu_model = model
  if (gpus !== askedGpus) body.gpus = gpus
  if (memoryGb !== '' && memoryGb !== askedMemoryGb) body.memory_mb = Number(memoryGb) * 1024
  const moved = body.gpu_model !== undefined || body.gpus !== undefined || body.memory_mb !== undefined
  // 내보내는 카드가 요구하는 메모리. 아무것도 고치지 않으면 신청이 이미 그 하한을 지났으므로 다시 재지 않는다.
  const vramMb = models.find((m) => m.model === model)?.vramMb ?? 0
  const floorMb = moved ? gpuMemoryFloorMb(vramMb, gpus) : 0
  const memoryMb = body.memory_mb ?? d.memory_mb
  const memoryBad = floorMb > 0 && memoryMb !== undefined && memoryMb < floorMb

  const submit = async () => {
    if (memoryBad) return
    setBusy(true)
    setError(null)
    try {
      await leaseApi.approve(item.subject_id, moved ? body : undefined)
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <>
      <Facts
        rows={[
          ['코어', d.cores !== undefined ? `${d.cores}코어` : DASH],
          ['메모리', d.memory_mb !== undefined ? sizeMb(d.memory_mb) : DASH],
          ['디스크', d.disk_gb !== undefined ? size(d.disk_gb) : DASH],
          ['GPU', d.gpus ? `${d.gpus}장${d.gpu_model ? ` ${d.gpu_model}` : ''}` : DASH],
          // 신청이 노드를 지정했으면 승인한 신청은 그 노드를 기다린다(B-45).
          ...(d.pinned_node ? [['노드', d.pinned_node] as [string, string]] : []),
        ]}
      />
      <div className="grid gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="gpu-approve-model">GPU 모델</Label>
          <select id="gpu-approve-model" className={SELECT} value={model} disabled={busy || !measured} onChange={(e) => setModel(e.target.value)}>
            {models.map((m) => (
              <option key={m.model} value={m.model}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="gpu-approve-count">장 수</Label>
          <select id="gpu-approve-count" className={SELECT} value={gpus} disabled={busy || !measured} onChange={(e) => setGpus(Number(e.target.value))}>
            {COUNTS.map((n) => (
              <option key={n} value={n}>
                {n}장
              </option>
            ))}
          </select>
        </div>
        <NumberField
          id="gpu-approve-memory"
          label="메모리 (GB)"
          value={memoryGb}
          placeholder={askedMemoryGb}
          busy={busy || !measured}
          onChange={setMemoryGb}
          hint={memoryBad ? `이 카드에는 메모리가 ${size(gpuMemoryFloorGb(vramMb, gpus))} 이상 필요합니다.` : ''}
          bad={memoryBad}
        />
      </div>
      <Err error={measured ? null : catalog.error} />
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button type="button" disabled={busy || memoryBad} onClick={() => void submit()}>
          {busy ? '승인 중' : '승인'}
        </Button>
      </DialogFooter>
    </>
  )
}
